// ============================================================
// FILE: ResetPassword.js
// LOCATION: C:\gardenmarket\web\src\pages\public\ResetPassword.js
// ============================================================
// PURPOSE:
//   Landing page for the emailed reset link. Validates the token
//   on load, then collects and submits a new password.
//
// FLOW:
//   1. Read ?token= from the URL
//   2. GET /users/reset-password/validate — so an expired link
//      says so immediately, rather than after the user has typed
//      a new password twice
//   3. POST /users/reset-password
//   4. Send the user to /login — deliberately NOT auto-logged-in.
//      Logging in with the new password confirms it works, and a
//      stolen link never yields a live session.
// ============================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import client from '../../api/client';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPassword() {
    const [params]  = useSearchParams();
    const navigate  = useNavigate();
    const token     = params.get('token');

    const [checking,  setChecking]  = useState(true);
    const [validity,  setValidity]  = useState({ is_valid:false, reason:'invalid', first_name:null });
    const [password,  setPassword]  = useState('');
    const [confirm,   setConfirm]   = useState('');
    const [show,      setShow]      = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [error,     setError]     = useState('');
    const [done,      setDone]      = useState(false);

    // Validate the token as soon as the page loads.
    useEffect(() => {
        if (!token) {
            setValidity({ is_valid:false, reason:'missing', first_name:null });
            setChecking(false);
            return;
        }
        let cancelled = false;
        client.get('/users/reset-password/validate', { params: { token } })
            .then(r => { if (!cancelled) setValidity(r.data); })
            .catch(()  => { if (!cancelled) setValidity({ is_valid:false, reason:'invalid', first_name:null }); })
            .finally(() => { if (!cancelled) setChecking(false); });
        return () => { cancelled = true; };
    }, [token]);

    const handleSubmit = async () => {
        setError('');
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (password !== confirm) {
            setError('The two passwords do not match.');
            return;
        }
        setSaving(true);
        try {
            await client.post('/users/reset-password', { token, password });
            setDone(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not reset your password. The link may have expired.');
        } finally {
            setSaving(false);
        }
    };

    // ---------- Loading ----------
    if (checking) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <p style={styles.text}>Checking your link...</p>
                </div>
            </div>
        );
    }

    // ---------- Bad / expired / used token ----------
    if (!validity.is_valid) {
        const messages = {
            missing:  'This link is incomplete. Please use the full link from your email.',
            expired:  'This reset link has expired. Reset links are only valid for 1 hour.',
            used:     'This reset link has already been used. Request a new one if you still need to change your password.',
            inactive: 'This account is no longer active. Please get in touch if you think this is a mistake.',
            invalid:  'This reset link is not valid. It may have been superseded by a newer one.',
        };
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.icon}>⚠️</div>
                    <h1 style={styles.title}>Link not valid</h1>
                    <p style={styles.text}>{messages[validity.reason] || messages.invalid}</p>
                    <Link to="/forgot-password" style={styles.btnLink} className="gh-touch-target">
                        Request a new link
                    </Link>
                    <p style={styles.footer}>
                        <Link to="/login" style={styles.link}>Back to login</Link>
                    </p>
                </div>
            </div>
        );
    }

    // ---------- Success ----------
    if (done) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.icon}>✅</div>
                    <h1 style={styles.title}>Password updated</h1>
                    <p style={styles.text}>
                        You can now log in with your new password. Taking you to the
                        login page...
                    </p>
                    <Link to="/login" style={styles.btnLink} className="gh-touch-target">
                        Go to login
                    </Link>
                </div>
            </div>
        );
    }

    // ---------- Form ----------
    const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
    const mismatch = confirm.length > 0 && password !== confirm;

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <h1 style={styles.title}>
                    {validity.first_name ? `Hi ${validity.first_name}` : 'Set a new password'}
                </h1>
                <p style={styles.text}>Choose a new password for your account.</p>

                {error && <div style={styles.error}>{error}</div>}

                <label style={styles.label}>New password</label>
                <input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    style={{...styles.input, ...(tooShort ? styles.inputBad : {})}}
                    autoComplete="new-password"
                    autoFocus
                />
                <p style={tooShort ? styles.hintBad : styles.hint}>
                    At least {MIN_PASSWORD_LENGTH} characters.
                </p>

                <label style={styles.label}>Confirm new password</label>
                <input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    style={{...styles.input, ...(mismatch ? styles.inputBad : {})}}
                    autoComplete="new-password"
                />
                {mismatch && <p style={styles.hintBad}>Passwords do not match.</p>}

                <label style={styles.checkboxLabel}>
                    <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
                    {' '}Show passwords
                </label>

                <button
                    onClick={handleSubmit}
                    style={styles.btn}
                    className="gh-touch-target"
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Set new password'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    page:          { maxWidth:'440px', margin:'0 auto', padding:'48px 24px' },
    card:          { background:'white', borderRadius:'12px', padding:'32px', boxShadow:'0 2px 12px rgba(0,0,0,0.08)' },
    icon:          { fontSize:'2.5rem', textAlign:'center', marginBottom:'8px' },
    title:         { color:'#2d6a4f', fontSize:'1.5rem', marginTop:0, marginBottom:'12px', textAlign:'center' },
    text:          { color:'#555', fontSize:'0.92rem', lineHeight:1.6, marginBottom:'20px', textAlign:'center' },
    label:         { display:'block', fontWeight:600, marginBottom:'6px', color:'#333', fontSize:'0.88rem' },
    // 16px font size prevents iOS Safari from zooming on focus
    input:         { width:'100%', padding:'11px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'16px', boxSizing:'border-box', marginBottom:'4px' },
    inputBad:      { borderColor:'#e57373' },
    hint:          { fontSize:'0.78rem', color:'#999', marginTop:0, marginBottom:'16px' },
    hintBad:       { fontSize:'0.78rem', color:'#c0392b', marginTop:0, marginBottom:'16px' },
    checkboxLabel: { fontSize:'0.85rem', color:'#555', display:'flex', alignItems:'center', gap:'6px', marginBottom:'20px' },
    btn:           { width:'100%', padding:'13px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer' },
    btnLink:       { display:'block', textAlign:'center', padding:'13px', backgroundColor:'#2d6a4f', color:'white', borderRadius:'8px', fontSize:'0.95rem', fontWeight:600, textDecoration:'none' },
    error:         { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:'6px', padding:'10px 14px', color:'#c0392b', marginBottom:'16px', fontSize:'0.88rem' },
    footer:        { textAlign:'center', marginTop:'20px', marginBottom:0, fontSize:'0.88rem', color:'#666' },
    link:          { color:'#2d6a4f', fontWeight:600, textDecoration:'none' },
};
